import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import IconButton from './IconButton';

type PageShellProps = {
  title: ReactNode;
  /** Подзаголовок под заголовком (опционально). */
  subtitle?: ReactNode;
  /**
   * Если задан — слева от заголовка появляется кнопка «Назад».
   * Строка → navigate(to); функция → вызов. -1 → история назад.
   */
  backTo?: string | (() => void);
  /** Узкая зона между заголовком и контентом — фильтры, поиск, табы. */
  toolbar?: ReactNode;
  /** Действия в правой части заголовка (CTA, дроп-меню). */
  actions?: ReactNode;
  /** Содержимое страницы. Растягивается на 100% оставшейся ширины и высоты. */
  children: ReactNode;
  /**
   * Снимает внутренние паддинги контент-зоны. Используй для таблиц / редакторов,
   * которым нужно прижаться к границам.
   */
  noContentPadding?: boolean;
  /**
   * По умолчанию контент-зона имеет `overflow: auto` и прокручивает всё внутри
   * (страница как одно полотно). Поставь `false`, если хочешь, чтобы прокрутка
   * жила внутри собственного блока (например, скроллится только таблица, а
   * фильтры остаются на месте).
   */
  bodyScrolls?: boolean;
};

/**
 * Эталон страницы внутри новой оболочки. Геометрия:
 *
 *   ┌─────────────────────────────────────────────────────┐
 *   │  Заголовок              Toolbar       Действия      │  header
 *   ├─────────────────────────────────────────────────────┤
 *   │                                                     │
 *   │   <content — 100% × 100%>                           │
 *   │                                                     │
 *   └─────────────────────────────────────────────────────┘
 *
 * MainLayoutV2 уже задаёт паддинг 20px по периметру; этот компонент
 * заполняет всё доступное пространство этой зоны.
 */
const PageShell = ({
  title,
  subtitle,
  backTo,
  toolbar,
  actions,
  children,
  noContentPadding,
  bodyScrolls = true,
}: PageShellProps) => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (typeof backTo === 'function') backTo();
    else if (typeof backTo === 'string') navigate(backTo);
    else navigate(-1);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        background: 'var(--shell-bg-surface)',
        border: '1px solid var(--shell-border-subtle)',
        borderRadius: 'var(--shell-radius-lg)',
        overflow: 'hidden',
        minHeight: 0,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          padding: '14px 20px',
          borderBottom: '1px solid var(--shell-border-subtle)',
          background: 'var(--shell-bg-surface)',
          flexShrink: 0,
        }}
      >
        {/* Строка 1: заголовок + действия */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            minHeight: 28,
          }}
        >
          {backTo !== undefined && (
            <IconButton
              label="Назад"
              onClick={handleBack}
              icon={<ArrowLeft size={16} strokeWidth={1.75} />}
            />
          )}
          <div style={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
            <div
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: 'var(--shell-text-primary)',
                lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {title}
            </div>
            {subtitle && (
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--shell-text-tertiary)',
                  marginTop: 2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {subtitle}
              </div>
            )}
          </div>

          {actions && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexShrink: 0,
                marginLeft: 'auto',
              }}
            >
              {actions}
            </div>
          )}
        </div>

        {/* Строка 2: тулбар / фильтры — на всю ширину */}
        {toolbar && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
              minWidth: 0,
            }}
          >
            {toolbar}
          </div>
        )}
      </div>

      {/* Content */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          padding: noContentPadding ? 0 : 16,
          overflow: bodyScrolls ? 'auto' : 'hidden',
          background: 'var(--shell-bg-surface)',
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default PageShell;
